import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { pickupPointsApi, type AddExceptionDto, type UpdateExceptionDto } from '../api/pickup-points.api';
import type { PickupPoint, CreatePickupPointDto, UpdatePickupPointDto } from '../types';
import { getPickupPointMutationErrorMessage } from '../utils/pickupPointMutationError';

/** После смены точек самовывоза на бэке меняются isHidden/isOutOfStock у товаров — обновляем кэш списков. */
function invalidateProductsAfterPickupPointsChange(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
  queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
  queryClient.invalidateQueries({ queryKey: ['products', 'collections', 'counts'] });
  queryClient.invalidateQueries({ queryKey: ['product'] });
}

export const usePickupPoints = () => {
  return useQuery<PickupPoint[]>({
    queryKey: ['pickup-points'],
    queryFn: () => pickupPointsApi.getAll(),
  });
};

export const usePickupPoint = (id: number) => {
  return useQuery<PickupPoint>({
    queryKey: ['pickup-points', id],
    queryFn: () => pickupPointsApi.getById(id),
    enabled: !!id,
  });
};

export const usePickupPointProductStocks = (pickupPointId: number, enabled = true) => {
  return useQuery({
    queryKey: ['pickup-points', pickupPointId, 'product-stocks'],
    queryFn: () => pickupPointsApi.getProductStocks(pickupPointId),
    enabled: !!pickupPointId && enabled,
  });
};

export const useCreatePickupPoint = () => {
  const queryClient = useQueryClient();

  return useMutation<PickupPoint, Error, CreatePickupPointDto>({
    mutationFn: (data) => pickupPointsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pickup-points'] });
      invalidateProductsAfterPickupPointsChange(queryClient);
      notifications.show({
        title: 'Успешно',
        message: 'Точка самовывоза успешно создана',
        color: 'green',
      });
    },
    onError: (error) => {
      const apiMessage = (error as { response?: { data?: { message?: string | string[] } } })
        ?.response?.data?.message;
      const message = Array.isArray(apiMessage)
        ? apiMessage[0]
        : apiMessage;
      notifications.show({
        title: 'Ошибка',
        message: message || 'Ошибка при создании точки самовывоза',
        color: 'red',
      });
    },
  });
};

export const useUpdatePickupPoint = () => {
  const queryClient = useQueryClient();

  return useMutation<
    PickupPoint,
    Error,
    { id: number; data: UpdatePickupPointDto; silent?: boolean }
  >({
    mutationFn: ({ id, data }) => pickupPointsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pickup-points'] });
      queryClient.invalidateQueries({ queryKey: ['pickup-points', variables.id] });
      if (variables.silent) return;
      notifications.show({
        title: 'Успешно',
        message: 'Точка самовывоза успешно обновлена',
        color: 'green',
      });
    },
    onError: (error, variables) => {
      if (variables.silent) return;
      notifications.show({
        title: 'Ошибка',
        message: getPickupPointMutationErrorMessage(
          error,
          'Ошибка при обновлении точки самовывоза',
        ),
        color: 'red',
      });
    },
  });
};

export const useDeletePickupPoint = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => pickupPointsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pickup-points'] });
      invalidateProductsAfterPickupPointsChange(queryClient);
      notifications.show({
        title: 'Успешно',
        message: 'Точка самовывоза успешно удалена',
        color: 'green',
      });
    },
    onError: (error) => {
      notifications.show({
        title: 'Ошибка',
        message: getPickupPointMutationErrorMessage(
          error,
          'Ошибка при удалении точки самовывоза',
        ),
        color: 'red',
      });
    },
  });
};

export const useAddException = () => {
  const queryClient = useQueryClient();

  return useMutation<PickupPoint, Error, { id: number; data: AddExceptionDto }>({
    mutationFn: ({ id, data }) => pickupPointsApi.addException(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pickup-points'] });
      queryClient.invalidateQueries({ queryKey: ['pickup-points', variables.id] });
      notifications.show({
        title: 'Успешно',
        message: 'Исключение успешно добавлено',
        color: 'green',
      });
    },
    onError: (error) => {
      notifications.show({
        title: 'Ошибка',
        message: getPickupPointMutationErrorMessage(
          error,
          'Ошибка при добавлении исключения',
        ),
        color: 'red',
      });
    },
  });
};

export const useUpdateException = () => {
  const queryClient = useQueryClient();

  return useMutation<PickupPoint, Error, { id: number; exceptionIndex: number; data: UpdateExceptionDto }>({
    mutationFn: ({ id, exceptionIndex, data }) => pickupPointsApi.updateException(id, exceptionIndex, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pickup-points'] });
      queryClient.invalidateQueries({ queryKey: ['pickup-points', variables.id] });
      notifications.show({
        title: 'Успешно',
        message: 'Исключение успешно обновлено',
        color: 'green',
      });
    },
    onError: (error) => {
      notifications.show({
        title: 'Ошибка',
        message: getPickupPointMutationErrorMessage(
          error,
          'Ошибка при обновлении исключения',
        ),
        color: 'red',
      });
    },
  });
};

export const useDeleteException = () => {
  const queryClient = useQueryClient();

  return useMutation<PickupPoint, Error, { id: number; exceptionIndex: number }>({
    mutationFn: ({ id, exceptionIndex }) => pickupPointsApi.deleteException(id, exceptionIndex),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pickup-points'] });
      queryClient.invalidateQueries({ queryKey: ['pickup-points', variables.id] });
      notifications.show({
        title: 'Успешно',
        message: 'Исключение успешно удалено',
        color: 'green',
      });
    },
    onError: (error) => {
      notifications.show({
        title: 'Ошибка',
        message: getPickupPointMutationErrorMessage(
          error,
          'Ошибка при удалении исключения',
        ),
        color: 'red',
      });
    },
  });
};
